import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useT } from '../i18n';
import { subPagesEs } from '../subpages_es';
import RelatedResources from './RelatedResources';
import ContinueExploring from './ContinueExploring';
import HeroImage from './HeroImage';
import { heroFor } from '../data/serviceImages';
import FlagshipCard from './FlagshipCard';

export default function SubPage({ data, heroVisual, showcase }) {
  const { t, lang } = useT();
  const es = lang === 'es' ? subPagesEs[data.slug] : null;

  // Merge English data with Spanish overrides (Spanish keeps icons, hrefs and images from English)
  const merged = es
    ? {
        ...data,
        eyebrow: es.eyebrow || data.eyebrow,
        title: es.title || data.title,
        intro: es.intro || data.intro,
        sub: es.sub || data.sub,
        primaryCta: { ...data.primaryCta, label: es.primaryCta?.label || data.primaryCta.label },
        secondaryCta: { ...data.secondaryCta, label: es.secondaryCta?.label || data.secondaryCta.label },
        pillarsTitle: es.pillarsTitle || data.pillarsTitle,
        pillarsIntro: es.pillarsIntro || data.pillarsIntro,
        pillars: data.pillars.map((p, i) => ({ ...p, ...(es.pillars?.[i] || {}) })),
        benefitsTitle: es.benefitsTitle || data.benefitsTitle,
        benefits: data.benefits.map((b, i) => ({ ...b, ...(es.benefits?.[i] || {}) })),
        bulletTitle: es.bulletTitle || data.bulletTitle,
        bullets: es.bullets || data.bullets,
      }
    : data;

  return (
    <div className="pt-28">
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-orange-50/60 to-white pt-8 pb-14 md:py-16 lg:py-24">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <p className="kx-eyebrow mb-5">
              {merged.eyebrow}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-bold text-neutral-900 leading-[1.05] tracking-[-0.02em]">
              {merged.title}
            </h1>
            <p className="mt-6 text-neutral-600 text-base sm:text-lg leading-relaxed max-w-xl">
              {merged.intro}
            </p>
            <p className="mt-4 text-neutral-500 text-sm leading-relaxed max-w-xl">{merged.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                className="bg-[#F77F11] hover:bg-[#d8651a] text-white px-6 h-12 rounded-md text-[15px] font-semibold"
              >
                <Link to={merged.primaryCta.href}>
                  {merged.primaryCta.label} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-[#F77F11] text-[#F77F11] hover:bg-orange-50 hover:text-[#d8651a] px-6 h-12 rounded-md text-[15px] font-semibold"
              >
                <Link to={merged.secondaryCta.href}>
                  {merged.secondaryCta.label} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
          <div className="relative lg:col-span-5">
            {heroVisual || (
              <HeroImage
                image={
                  heroFor(data.slug) || {
                    src: merged.image,
                    alt: merged.title,
                  }
                }
              />
            )}
          </div>
        </div>
      </section>

      {showcase}

      {/* Pillars */}
      <section id="systems" className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="kx-eyebrow mb-4">
              {lang === 'es' ? 'PLATAFORMA TECNOLÓGICA' : 'RESTAURANT TECHNOLOGY PLATFORM'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
              {merged.pillarsTitle}
            </h2>
            <p className="mt-4 text-neutral-600">{merged.pillarsIntro}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {merged.pillars.map((p) => {
              const I = Icons[p.icon] || Icons.Sparkles;
              const Card = (
                <div className="group rounded-2xl border border-neutral-200 hover:border-[#F77F11] bg-white p-7 transition-all hover:shadow-xl h-full">
                  <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5 group-hover:bg-[#F77F11] transition-colors">
                    <I className="w-6 h-6 text-[#F77F11] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{p.text}</p>
                  {p.href && (
                    <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#F77F11]">
                      {t('common.learnMore')} <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </div>
              );
              return p.href ? (
                <Link key={p.title} to={p.href} className="block">
                  {Card}
                </Link>
              ) : (
                <div key={p.title}>{Card}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25% 30%, #F77F11 1px, transparent 1.5px), radial-gradient(circle at 75% 70%, #F77F11 1px, transparent 1.5px)',
            backgroundSize: '140px 140px, 180px 180px',
          }}
        />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="kx-eyebrow mb-4">
              {lang === 'es' ? 'RESULTADOS MEDIBLES' : 'MEASURABLE RESULTS'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{merged.benefitsTitle}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {merged.benefits.map((b) => {
              const I = Icons[b.icon] || Icons.Sparkles;
              return (
                <div
                  key={b.title}
                  className="rounded-2xl bg-neutral-900/70 border border-neutral-800 hover:border-[#F77F11] transition-colors p-6"
                >
                  <I className="w-7 h-7 text-[#F77F11] mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{b.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contextual flagship micro-promotion */}
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10 pt-14">
        <FlagshipCard context={data.slug} />
      </div>

      {/* Bullets + CTA */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-white to-orange-50/40">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="kx-eyebrow mb-4">
              {lang === 'es'
                ? 'SISTEMA INTEGRAL DE CRECIMIENTO'
                : 'END-TO-END RESTAURANT GROWTH SYSTEM'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
              {merged.bulletTitle}
            </h2>
            <ul className="mt-7 space-y-3">
              {merged.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-[#F77F11] shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <Button
              asChild
              className="mt-8 bg-[#F77F11] hover:bg-[#d8651a] text-white px-6 h-12 rounded-md text-[15px] font-semibold"
            >
              <Link to="/#audit">
                {t('common.requestAudit')} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-xl border border-orange-100 aspect-[4/3]">
            <img
              src={
                merged.bottomImage ||
                'https://static.wixstatic.com/media/c99602_230d590909ad497cbfeddbcfa338e18f~mv2.png/v1/fill/w_1200,h_900,al_c,q_85,enc_avif,quality_auto/file.png'
              }
              alt="Kitxens"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <RelatedResources serviceSlug={data.slug} lang={lang} />
      <ContinueExploring context={data.slug} />
    </div>
  );
}
